import { useQuery } from 'react-query';
import { chamarOktonBank } from '../oktonbank';

// O back devolve os dois lados já resolvidos e achatados num registro só:
// evita uma segunda consulta por linha para montar a tela.
export interface Conciliado {
  id_cbc: string;
  filial_cbc: string;
  extrato_cbc: string;
  // O documento conciliado é um par: tipo_cbc diz o que é, documento_cbc
  // diz qual. Só vêm preenchidos os campos do tipo daquela linha.
  tipo_cbc: string;
  documento_cbc: string;
  valor_cbc: string;
  origem_cbc: string;
  cadastro_cbc: string;
  data_exb: string;
  descricao_exb: string;
  valor_exb: string;
  referencia_exb: string | null;
  conta_exb: string;
  descricao_cts: string | null;
  documento_fin: string | null;
  baixa_fin: string;
  tipo_fin: string;
  valorbaixa_fin: string;
  observacao_fin: string | null;
  centrocusto_fin: string | null;
  documento_tba: string | null;
  observacao_tba: string | null;
  origem_tba: string | null;
  destino_tba: string | null;
  observacao_saq: string | null;
  observacao_dep: string | null;
  envelope_dep: string | null;
  // O que os documentos vinculados ao extrato valem de fato (a baixa do
  // título, o Saque...), somados. É o que decide a divergência.
  totaldocumentos_cbc: string;
  // '1' quando o extrato fechou sem os documentos somarem o valor dele.
  divergente_cbc: string;
}

interface IResponseData {
  rows: Conciliado[];
}

// origem_cbc: '1' manual, '2' automática.
export const ORIGEM_MANUAL = '1';
export const ORIGEM_AUTOMATICA = '2';

// tipo_cbc: espelha as constantes C_TIPOCONCILIACAO_* do IFLEX. A
// Transferência tem dois tipos porque aparece nos dois extratos — a saída
// na conta de origem e a entrada na de destino são fatos distintos.
export const TIPO_FINANCEIRO = '1';
export const TIPO_TRANSFERENCIA_ORIGEM = '2';
export const TIPO_TRANSFERENCIA_DESTINO = '3';
export const TIPO_SAQUE = '4';
export const TIPO_DEPOSITO = '5';

export function ehTransferencia(tipo: string) {
  return (
    tipo === TIPO_TRANSFERENCIA_ORIGEM || tipo === TIPO_TRANSFERENCIA_DESTINO
  );
}

// O outro lado do vínculo, do jeito que a tela mostra. Só o título tem
// valor próprio para comparar; nos demais o valor casado é o do vínculo.
export function descreveDocumento(conciliado: Conciliado) {
  if (ehTransferencia(conciliado.tipo_cbc)) {
    return {
      titulo:
        conciliado.observacao_tba ||
        conciliado.documento_tba ||
        'Transferência',
      detalhe: `Transferência: ${conciliado.origem_tba ?? '?'} → ${
        conciliado.destino_tba ?? '?'
      }`,
    };
  }
  if (conciliado.tipo_cbc === TIPO_SAQUE) {
    return {
      titulo: conciliado.observacao_saq || 'Saque',
      detalhe: 'Saque em conta',
    };
  }
  if (conciliado.tipo_cbc === TIPO_DEPOSITO) {
    return {
      titulo: conciliado.observacao_dep || 'Depósito',
      detalhe: conciliado.envelope_dep
        ? `Depósito • Envelope ${conciliado.envelope_dep}`
        : 'Depósito em conta',
    };
  }
  return { titulo: conciliado.observacao_fin ?? '', detalhe: '' };
}

// Mesmos filtros da aba de pendentes — Conta e período do extrato —, para as
// duas abas falarem do mesmo recorte. O período é o da linha do extrato: é a
// data do banco que o filtro da tela representa.
async function getConciliacoes(
  idConta?: string,
  dataIni = '',
  dataFim = '',
): Promise<Conciliado[]> {
  const dados = await chamarOktonBank<IResponseData>(
    'OBConciliacao',
    'listar',
    { conta: idConta ?? '', dataini: dataIni, datafim: dataFim },
  );
  return Array.isArray(dados?.rows) ? dados.rows : [];
}

// habilitado: a lista de conciliados e a maior das duas da tela, e so a aba
// Conciliados a mostra. Busca-la com a tela em Pendentes e uma ida ao servidor
// por nada - e cada ida ao acoes.php custa o tempo de subir o IFLEX inteiro.
export function useConciliacoes(
  idConta?: string,
  dataIni = '',
  dataFim = '',
  habilitado = true,
) {
  return useQuery(
    ['conciliacoes', idConta ?? '', dataIni, dataFim],
    () => getConciliacoes(idConta, dataIni, dataFim),
    { enabled: habilitado },
  );
}
